// Cart Options Extension - Extends cart.js functionality
class CartOptionsExtension {
  constructor() {
    this.selectedPackagingOption = null;
    this.selectedVariantId = null; // Track selected variant ID
    this.currentGiftWrapVariantId = null;
    this.packagingVariantIds = new Set(); // Track packaging variant IDs
    this.isOperationInProgress = false; // Prevent overlapping operations
    this.operationQueue = []; // Queue for operations
    this.eventListeners = new Map(); // Track event listeners for cleanup
    this.isSwitchingPackaging = false; // Flag to prevent state restoration during switching
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }

    // Listen for cart.js updates to restore our state
    if (typeof subscribe === 'function') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'cart-options') {
          setTimeout(() => {
            if (!this.isSwitchingPackaging) {
              this.setupEventListeners();
              this.handleManualCartItemRemoval();
              this.validateCartState();
              this.hideQuantitySelectorsForSpecialItems();
              
              // Only ensure default packaging if gift wrap is NOT selected
              if (!this.isGiftWrapSelected()) {
                this.ensureDefaultPackaging();
              }
              
              // Add longer delay for ensureSinglePackagingOption to avoid interfering with gift wrap
              setTimeout(() => {
                this.ensureSinglePackagingOption();
              }, 1000);
            } else {
              this.setupEventListeners();
              this.hideQuantitySelectorsForSpecialItems();
            }
            
            // Update active classes for packaging options with longer delay to ensure DOM is updated
            setTimeout(() => {
              this.updatePackagingActiveClasses();
            }, 500);
            
            // Validate cart has main products after any cart update
            setTimeout(() => {
              this.validateCartHasMainProduct();
            }, 1000);
          }, 100);
        }
      });
    }

    // Start periodic state validation
    this.startStateValidation();

    // Add immediate active class check after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.updatePackagingActiveClasses();
    }, 1000);

    // Validate cart on page load to ensure no orphaned addon items
    setTimeout(() => {
      this.validateCartHasMainProduct();
    }, 1500);

    // Listen for cart drawer opening to update active classes
    this.setupCartDrawerListener();

    // Setup manual removal detection for gift wrap items
    this.setupManualRemovalDetection();

    // Setup cart drawer to page synchronization
    this.setupCartDrawerToPageSync();

    // Setup cart drawer quantity synchronization
    this.setupCartDrawerQuantitySync();
    
    // Make debugging functions available globally
    const debugFunctions = {
      forceCartRefresh: () => this.forceRefreshCartState(),
      removeAllGiftWrap: () => this.removeAllGiftWrapItems(),
      forceCorrectState: () => this.forceCorrectState(),
      smartRemoveGiftWrap: () => this.smartRemoveGiftWrap(),
      ultraRemoveGiftWrap: () => this.ultraRemoveGiftWrap(),
      nuclearRemoveGiftWrap: () => this.nuclearRemoveGiftWrap(),
      updateWithDelay: () => this.updatePackagingActiveClassesWithDelay(),
      setupManualDetection: () => this.setupManualRemovalDetection(),
      validateCart: () => this.validateCartHasMainProduct(),
      removeAllAddons: () => this.removeAllAddonItems(),
      syncCartPage: () => this.updateCartPageSections()
    };
    
    Object.entries(debugFunctions).forEach(([name, func]) => {
      window[name] = () => {
        if (window.cartOptionsExtension) {
          func.call(window.cartOptionsExtension);
        }
      };
    });
  }

  // Add operation queuing to prevent race conditions
  async queueOperation(operation) {
    if (this.isOperationInProgress) {
      return new Promise((resolve, reject) => {
        this.operationQueue.push({ operation, resolve, reject });
      });
    }

    this.isOperationInProgress = true;

    try {
      const result = await operation();
      return result;
    } catch (error) {
      console.error('Operation failed:', error);
      this.recoverFromError();
      throw error;
    } finally {
      this.isOperationInProgress = false;

      // Process next operation in queue
      if (this.operationQueue.length > 0) {
        const nextOp = this.operationQueue.shift();
        setTimeout(() => {
          this.queueOperation(nextOp.operation)
            .then(nextOp.resolve)
            .catch(nextOp.reject);
        }, 100); // Reduced delay for better responsiveness
      }
    }
  }

  setupEventListeners() {
    // Remove existing event listeners to prevent duplicates
    this.removeEventListeners();

    // Setup packaging option selection (radio button behavior)
    this.setupPackagingOptions();

    // Setup donation checkbox
    this.setupDonationOption();

    // Restore selected state after DOM updates
    this.restoreSelectedState();

    // Update active classes to ensure correct initial state
    this.updatePackagingActiveClasses();

    // Ensure default complementary packaging is added (with delay to avoid conflicts)
    setTimeout(() => {
      this.ensureDefaultPackaging();
    }, 200);

  }

  removeEventListeners() {
    // Remove existing event listeners from packaging checkboxes
    document.querySelectorAll('.luxury-option-checkbox').forEach(checkbox => {
      const newCheckbox = checkbox.cloneNode(true);
      checkbox.parentNode.replaceChild(newCheckbox, checkbox);
    });

    // Remove existing event listeners from donation checkbox
    const donationCheckbox = document.getElementById('cart-donation');
    if (donationCheckbox) {
      const newCheckbox = donationCheckbox.cloneNode(true);
      donationCheckbox.parentNode.replaceChild(newCheckbox, donationCheckbox);
    }

    // Clean up tracked event listeners
    this.eventListeners.forEach((listener, element) => {
      if (element && element.removeEventListener) {
        element.removeEventListener('change', listener);
      }
    });
    this.eventListeners.clear();
  }

  // Add state validation to ensure UI consistency
  validateCartState() {
    
    const cartItems = document.querySelectorAll('.cart-item');
    const packagingCheckboxes = document.querySelectorAll('.luxury-option-checkbox:checked');
    const donationCheckbox = document.getElementById('cart-donation');

    // Validate packaging checkboxes match cart contents
    packagingCheckboxes.forEach(checkbox => {
      const variantId = checkbox.dataset.variantId;
      const existingCartItem = this.findCartItemByVariantId(variantId);

      if (!existingCartItem) {
        console.warn('Packaging checkbox checked but item not in cart, fixing...');
        checkbox.checked = false;
      }
    });

    // Validate donation checkbox matches cart contents
    if (donationCheckbox && donationCheckbox.checked) {
      const variantId = donationCheckbox.dataset.variantId;
      const existingCartItem = this.findCartItemByVariantId(variantId);

      if (!existingCartItem) {
        console.warn('Donation checkbox checked but item not in cart, fixing...');
        donationCheckbox.checked = false;
      }
    }

    // Additional validation: check if gift wrap checkbox state matches actual cart contents
    const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
    if (giftWrapCheckbox) {
      const giftWrapInCart = Array.from(cartItems).some(item => {
        const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
        return itemTitle.toLowerCase().includes('gift wrap');
      });
      
      if (giftWrapCheckbox.checked !== giftWrapInCart) {
        console.warn('Gift wrap checkbox state mismatch detected, fixing...');
        giftWrapCheckbox.checked = giftWrapInCart;
        
        // Update active classes after fixing checkbox state
        setTimeout(() => {
          this.updatePackagingActiveClasses();
        }, 100);
      }
    }

  }

  restoreSelectedState() {
    
    try {
      // Restore packaging selection based on cart contents
      this.restorePackagingSelection();

      // Restore donation selection based on cart contents
      this.restoreDonationSelection();

      // Restore packaging tracking from cart contents
      this.restorePackagingTracking();

      // Restore donation tracking from cart contents
      this.restoreDonationTracking();

      // Validate state after restoration
      this.validateCartState();
    } catch (error) {
      console.error('Error restoring selected state:', error);
      this.recoverFromError();
    }
  }

  restorePackagingSelection() {
    // Don't restore if we're in the middle of a packaging switch
    if (this.isSwitchingPackaging) {
      return;
    }

    const cartItems = document.querySelectorAll('.cart-item');
    let foundPackagingItems = [];

    cartItems.forEach(item => {
      const variantId = item.getAttribute('data-variant-id');
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';

      if (variantId && variantId !== 'null' && variantId !== 'undefined') {
        const isPackagingItem = itemTitle.toLowerCase().includes('packaging') ||
          itemTitle.toLowerCase().includes('gift wrap') ||
          itemTitle.toLowerCase().includes('wrapping') ||
          itemTitle.toLowerCase().includes('gift') ||
          itemTitle.toLowerCase().includes('luxury') ||
          itemTitle.toLowerCase().includes('gift wrap service') ||
          itemTitle.toLowerCase().includes('gift wrapping');

        if (isPackagingItem) {
          foundPackagingItems.push({ variantId, itemTitle });
        }
      }
    });

    // Only restore if we have exactly one packaging item
    if (foundPackagingItems.length === 1) {
      const foundPackagingItem = foundPackagingItems[0];
      // Find the corresponding packaging checkbox and check it
      const packagingCheckbox = document.querySelector(`.luxury-option-checkbox[data-variant-id="${foundPackagingItem.variantId}"]`);
      if (packagingCheckbox) {
        
        // Uncheck all packaging checkboxes first
        document.querySelectorAll('.luxury-option-checkbox').forEach(checkbox => {
          checkbox.checked = false;
        });

        // Check the found checkbox
        packagingCheckbox.checked = true;
        this.selectedVariantId = foundPackagingItem.variantId;

        // If it's gift wrap, show the message area
        const optionType = packagingCheckbox.dataset.option;
        if (optionType === 'giftwrap') {
          const card = packagingCheckbox.closest('.luxury-option-card');
          if (card) {
            this.showGiftWrapMessageArea(card, foundPackagingItem.variantId);
          }
        }
      }
    } else if (foundPackagingItems.length > 1) {
      console.warn(`Multiple packaging items found in cart (${foundPackagingItems.length}), not restoring selection to prevent conflicts`);
      // Uncheck all packaging checkboxes to prevent multiple selections
      document.querySelectorAll('.luxury-option-checkbox').forEach(checkbox => {
        checkbox.checked = false;
      });
    } else {
      // Uncheck all packaging checkboxes
      document.querySelectorAll('.luxury-option-checkbox').forEach(checkbox => {
        checkbox.checked = false;
      });
    }
  }

  restoreDonationSelection() {
    const cartItems = document.querySelectorAll('.cart-item');
    let foundDonationItem = null;

    cartItems.forEach(item => {
      const variantId = item.getAttribute('data-variant-id');
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';

      if (variantId && variantId !== 'null' && variantId !== 'undefined') {
        const isDonationItem = itemTitle.toLowerCase().includes('donation') ||
          itemTitle.toLowerCase().includes('charity') ||
          itemTitle.toLowerCase().includes('support');

        if (isDonationItem) {
          foundDonationItem = { variantId, itemTitle };
        }
      }
    });

    if (foundDonationItem) {
      // Find the donation checkbox and check it
      const donationCheckbox = document.getElementById('cart-donation');
      if (donationCheckbox && donationCheckbox.dataset.variantId === foundDonationItem.variantId) {
        donationCheckbox.checked = true;
      }
    }
  }

  restorePackagingTracking() {
    // Check cart contents and restore packaging tracking
    const cartItems = document.querySelectorAll('.cart-item');
    
    cartItems.forEach(item => {
      const variantId = item.getAttribute('data-variant-id');
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';

      if (variantId) {
        const isPackagingByTitle = itemTitle.toLowerCase().includes('packaging') ||
          itemTitle.toLowerCase().includes('gift wrap') ||
          itemTitle.toLowerCase().includes('wrapping') ||
          itemTitle.toLowerCase().includes('gift') ||
          itemTitle.toLowerCase().includes('luxury') ||
          itemTitle.toLowerCase().includes('gift wrap service') ||
          itemTitle.toLowerCase().includes('gift wrapping');

        if (isPackagingByTitle) {
          this.packagingVariantIds.add(variantId);
        }
      }
    });

  }

  restoreDonationTracking() {
    // Check cart contents and restore donation tracking
    const cartItems = document.querySelectorAll('.cart-item');

    this.donationVariantIds = this.donationVariantIds || new Set();

    cartItems.forEach(item => {
      const variantId = item.getAttribute('data-variant-id');
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';

      if (variantId) {
        const isDonationByTitle = itemTitle.toLowerCase().includes('donation') ||
          itemTitle.toLowerCase().includes('charity') ||
          itemTitle.toLowerCase().includes('support');

        if (isDonationByTitle) {
          this.donationVariantIds.add(variantId);
        }
      }
    });

   
    // Restore donation checkbox state
    this.restoreDonationCheckboxState();
  }

  restoreDonationCheckboxState() {
    const donationCheckbox = document.getElementById('cart-donation');
    if (donationCheckbox) {
      const variantId = donationCheckbox.dataset.variantId;

      // Check if donation item exists in cart
      const existingDonationItem = this.findCartItemByVariantId(variantId);
      const isDonationInCart = existingDonationItem !== null;

      // Update checkbox state to match cart state
      donationCheckbox.checked = isDonationInCart;

    }
  }

  applySelectedState(card) {
    // Apply visual selected state without triggering cart operations
    card.classList.add('selected');
    const btn = card.querySelector('.luxury-option-select-btn');
    if (btn) {
      btn.textContent = 'Unselect';
      btn.classList.add('selected-btn');
    }
    this.selectedPackagingOption = card;
  }

  findCartItemByVariantId(variantId) {
    const cartItems = document.querySelectorAll('.cart-item');
    
    for (const item of cartItems) {
      const itemVariantId = item.getAttribute('data-variant-id');
      const itemTitle = item.querySelector('.cart-item__name')?.textContent || '';
    
      if (itemVariantId === variantId) {
        return item;
      }
    }

    return null;
  }

  findAnyPackagingItem() {
    const cartItems = document.querySelectorAll('.cart-item');

    for (const item of cartItems) {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      const variantId = item.getAttribute('data-variant-id');

      // Check if this is a packaging item by title
      const isPackagingItem = itemTitle.toLowerCase().includes('packaging') ||
        itemTitle.toLowerCase().includes('gift wrap') ||
        itemTitle.toLowerCase().includes('wrapping') ||
        itemTitle.toLowerCase().includes('gift') ||
        itemTitle.toLowerCase().includes('luxury') ||
        itemTitle.toLowerCase().includes('gift wrap service') ||
        itemTitle.toLowerCase().includes('gift wrapping');

      if (isPackagingItem && variantId && variantId !== 'null' && variantId !== 'undefined') {
        return item;
      }
    }

    return null;
  }

  getPackagingTypeFromTitle(title) {
    const lowerTitle = title.toLowerCase();

    if (lowerTitle.includes('complementary') || lowerTitle.includes('complimentary')) {
      return 'Complementary Packaging';
    } else if (lowerTitle.includes('gift wrap') || lowerTitle.includes('gift wrapping')) {
      return 'Gift Wrap Service';
    } else {
      return 'Packaging';
    }
  }

  handleManualCartItemRemoval() {

    // Check if packaging items were removed
    const packagingCheckboxes = document.querySelectorAll('.luxury-option-checkbox:checked');
    let packagingRemoved = false;
    
    packagingCheckboxes.forEach(checkbox => {
      const variantId = checkbox.dataset.variantId;
      const existingCartItem = this.findCartItemByVariantId(variantId);

      if (!existingCartItem) {
        checkbox.checked = false;
        packagingRemoved = true;

        // Hide gift wrap message area if exists
        const card = checkbox.closest('.luxury-option-card');
        if (card) {
          const messageArea = card.querySelector('.gift-wrap-message-area');
          if (messageArea) {
            messageArea.style.display = 'none';
          }
        }
      }
    });

    // Check if donation item was removed
    const donationCheckbox = document.getElementById('cart-donation');
    if (donationCheckbox && donationCheckbox.checked) {
      const variantId = donationCheckbox.dataset.variantId;
      const existingCartItem = this.findCartItemByVariantId(variantId);

      if (!existingCartItem) {
        donationCheckbox.checked = false;
      }
    }
    
    // Update active classes if packaging was removed
    if (packagingRemoved) {
      this.updatePackagingActiveClasses();
    }
  }

  // Enhanced method to detect and handle manual gift wrap removal
  setupManualRemovalDetection() {
    
    // Listen for clicks on remove buttons
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('button[aria-label*="Remove"], button[aria-label*="Delete"], .cart-item__remove, .remove-button');
      if (removeButton) {
        
        // Find the cart item that contains this remove button
        const cartItem = removeButton.closest('.cart-item, tr[class*="cart-item"]');
        if (cartItem) {
          const itemTitle = cartItem.querySelector('.cart-item__name, .cart-item-name')?.textContent?.trim() || '';
          const variantId = cartItem.getAttribute('data-variant-id');
          
          // Check if this is a gift wrap item
          if (itemTitle.toLowerCase().includes('gift wrap')) {
            
            // Wait for the removal to complete, then update UI and validate cart
            setTimeout(() => {
              this.nuclearRemoveGiftWrap();
              this.validateCartHasMainProduct();
            }, 1000);
          }
        }
      }
    });
    
    // Also listen for quantity changes to 0 (another way to remove items)
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[type="number"], .quantity__input, .cart-item__quantity input')) {
        const quantity = parseInt(event.target.value);
        if (quantity === 0) {
          
          const cartItem = event.target.closest('.cart-item, tr[class*="cart-item"]');
          if (cartItem) {
            const itemTitle = cartItem.querySelector('.cart-item__name, .cart-item-name')?.textContent?.trim() || '';
            const variantId = cartItem.getAttribute('data-variant-id');
            
            if (itemTitle.toLowerCase().includes('gift wrap')) {
              
              // Wait for the removal to complete, then update UI and validate cart
              setTimeout(() => {
                this.nuclearRemoveGiftWrap();
                this.validateCartHasMainProduct();
              }, 1000);
            }
          }
        }
      }
    });
    
  }

  // Method to validate cart has at least one main product
  validateCartHasMainProduct() {
    const cartItems = document.querySelectorAll('.cart-item');
    let hasMainProduct = false;
    let hasOnlyAddonItems = true;
    
    cartItems.forEach(item => {
      const itemTitle = item.querySelector('.cart-item__name, .cart-item-name')?.textContent?.trim() || '';
      const variantId = item.getAttribute('data-variant-id');
      
      // Check if this is a main product (not gift wrap, donation, or complementary packaging)
      const isGiftWrap = itemTitle.toLowerCase().includes('gift wrap');
      const isDonation = itemTitle.toLowerCase().includes('donation');
      const isComplementary = itemTitle.toLowerCase().includes('complementary');
      
      // Skip items with null variant IDs (they're usually duplicates or invalid)
      if (variantId && variantId !== 'null') {
        if (!isGiftWrap && !isDonation && !isComplementary) {
          hasMainProduct = true;
          hasOnlyAddonItems = false;
        }
      }
    });
    
    // If cart has no main products, remove all addon items
    if (!hasMainProduct && hasOnlyAddonItems) {
      this.removeAllAddonItems();
    }
  }

  // Method to remove all addon items (gift wrap, donation, complementary packaging)
  removeAllAddonItems() {
    const cartItems = document.querySelectorAll('.cart-item');
    let removedCount = 0;
    
    cartItems.forEach(item => {
      const itemTitle = item.querySelector('.cart-item__name, .cart-item-name')?.textContent?.trim() || '';
      const variantId = item.getAttribute('data-variant-id');
      const isGiftWrap = itemTitle.toLowerCase().includes('gift wrap');
      const isDonation = itemTitle.toLowerCase().includes('donation');
      const isComplementary = itemTitle.toLowerCase().includes('complementary');
      
      // Only remove items with valid variant IDs (skip null variants)
      if ((isGiftWrap || isDonation || isComplementary) && variantId && variantId !== 'null') {
        const cartItemId = item.id;
        const lineNumber = cartItemId ? cartItemId.replace('CartItem-', '').replace('CartDrawer-Item-', '') : null;
        
        const cartItemsElement = document.querySelector('cart-items');
        if (cartItemsElement && lineNumber) {
          try {
            cartItemsElement.updateQuantity(lineNumber, 0);
            removedCount++;
          } catch (error) {
            console.error(`Error removing addon item at line ${lineNumber}:`, error);
          }
        }
      }
    });
    
    // Update UI state after removing addon items
    setTimeout(() => {
      this.updatePackagingActiveClasses();
      
      // Uncheck all addon checkboxes
      const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
      const donationCheckbox = document.getElementById('cart-donation');
      
      if (giftWrapCheckbox) {
        giftWrapCheckbox.checked = false;
      }
      
      if (donationCheckbox) {
        donationCheckbox.checked = false;
      }
    }, 1000);
  }

  removePackagingItemFromCart(cartItem) {

    const cartItemsElement = document.querySelector('cart-items');
    if (cartItemsElement) {
      // Get the correct line number from the cart item ID
      const cartItemId = cartItem.id;
      const lineNumber = cartItemId ? cartItemId.replace('CartItem-', '') : null;

      if (lineNumber) {
        try {
          // Use cart.js native updateQuantity method with quantity 0 to remove
          cartItemsElement.updateQuantity(lineNumber, 0);

          // The cart.js updateQuantity method will handle all the section updates automatically
          // including cart footer updates, so we don't need to do anything else here

          // Update active classes immediately after removal
          setTimeout(() => {
            this.updatePackagingActiveClasses();
          }, 100);

          // Re-setup event listeners after a delay to let cart.js complete its updates
          setTimeout(() => {
            this.setupEventListeners();
          }, 200);
        } catch (error) {
          console.error('Error removing packaging item:', error);
        }
      } else {
        console.warn('Cannot remove packaging item - missing line number');
      }
    } else {
      console.warn('Cannot remove packaging item - missing cart items element');
    }
  }

  // Enhanced method to remove ALL gift wrap items from cart
  removeAllGiftWrapItems() {
    
    const cartItems = document.querySelectorAll('.cart-item');
    let removedCount = 0;
    
    // First pass: try to remove items with valid line numbers
    cartItems.forEach((item, index) => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      const isGiftWrap = itemTitle.toLowerCase().includes('gift wrap');
      
      if (isGiftWrap) {
        
        const cartItemsElement = document.querySelector('cart-items');
        if (cartItemsElement) {
          const cartItemId = item.id;
          const lineNumber = cartItemId ? cartItemId.replace('CartItem-', '') : null;
          
          if (lineNumber) {
            try {
              cartItemsElement.updateQuantity(lineNumber, 0);
              removedCount++;
            } catch (error) {
              console.error(`Error removing gift wrap item at line ${lineNumber}:`, error);
            }
          } else {
            console.warn(`Cannot remove gift wrap item - missing line number for item ${index}`);
          }
        }
      }
    });
    
    
    // Second pass: force remove any remaining gift wrap items by setting quantity to 0
    setTimeout(() => {
      const remainingCartItems = document.querySelectorAll('.cart-item');
      let forceRemovedCount = 0;
      
      remainingCartItems.forEach((item, index) => {
        const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
        const isGiftWrap = itemTitle.toLowerCase().includes('gift wrap');
        
        if (isGiftWrap) {
          
          // Try to find any quantity input and set it to 0
          const quantityInput = item.querySelector('input[type="number"]');
          if (quantityInput) {
            quantityInput.value = 0;
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            forceRemovedCount++;
          }
        }
      });
      
    }, 1000);
    
    // Force uncheck the gift wrap checkbox immediately
    const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
    if (giftWrapCheckbox) {
      giftWrapCheckbox.checked = false;
    }
    
    // Update active classes after removal
    setTimeout(() => {
      this.updatePackagingActiveClasses();
    }, 500);
  }

  // Force correct state by checking actual cart contents
  forceCorrectState() {
    
    // Check if gift wrap is actually in cart
    const cartItems = document.querySelectorAll('.cart-item');
    const giftWrapInCart = Array.from(cartItems).some(item => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      return itemTitle.toLowerCase().includes('gift wrap');
    });
    
    
    // Force checkbox state to match cart
    const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
    if (giftWrapCheckbox) {
      giftWrapCheckbox.checked = giftWrapInCart;
    }
    
    // Update active classes
    this.updatePackagingActiveClasses();
  }

  // Nuclear option: force remove all gift wrap items by any means necessary
  forceRemoveAllGiftWrap() {
    
    const cartItems = document.querySelectorAll('.cart-item');
    let removedCount = 0;
    
    // Method 1: Try cart.js updateQuantity
    cartItems.forEach((item, index) => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      const isGiftWrap = itemTitle.toLowerCase().includes('gift wrap');
      
      if (isGiftWrap) {
        const cartItemsElement = document.querySelector('cart-items');
        if (cartItemsElement) {
          const cartItemId = item.id;
          const lineNumber = cartItemId ? cartItemId.replace('CartItem-', '') : null;
          
          if (lineNumber) {
            try {
              cartItemsElement.updateQuantity(lineNumber, 0);
              removedCount++;
            } catch (error) {
              console.error(`Nuclear removal: Error with updateQuantity:`, error);
            }
          }
        }
      }
    });
    
    // Method 2: Try direct quantity input manipulation
    setTimeout(() => {
      const remainingItems = document.querySelectorAll('.cart-item');
      remainingItems.forEach((item, index) => {
        const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
        const isGiftWrap = itemTitle.toLowerCase().includes('gift wrap');
        
        if (isGiftWrap) {
          // Try multiple selectors for quantity inputs
          const quantitySelectors = [
            'input[type="number"]',
            '.quantity__input',
            '.cart-item__quantity input',
            'input[name="quantity"]'
          ];
          
          quantitySelectors.forEach(selector => {
            const quantityInput = item.querySelector(selector);
            if (quantityInput) {
              quantityInput.value = 0;
              quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
              quantityInput.dispatchEvent(new Event('input', { bubbles: true }));

            }
          });
        }
      });
    }, 500);
    
    // Method 3: Force checkbox state and active classes
    setTimeout(() => {
      const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
      if (giftWrapCheckbox) {
        giftWrapCheckbox.checked = false;
      }
      
      this.updatePackagingActiveClasses();
    }, 1000);
    
  }

  // Smart removal: Handle null variant IDs and duplicate items
  smartRemoveGiftWrap() {
    
    const cartItems = document.querySelectorAll('.cart-item');
    let removedCount = 0;
    
    // First, identify all gift wrap items (including those with null variant IDs)
    const giftWrapItems = Array.from(cartItems).filter(item => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      return itemTitle.toLowerCase().includes('gift wrap');
    });
    
    
    // Remove each gift wrap item by its position in the cart
    giftWrapItems.forEach((item, index) => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      const variantId = item.getAttribute('data-variant-id');
      const cartItemId = item.id;
      const lineNumber = cartItemId ? cartItemId.replace('CartItem-', '') : null;
      
      
      const cartItemsElement = document.querySelector('cart-items');
      if (cartItemsElement && lineNumber) {
        try {
          cartItemsElement.updateQuantity(lineNumber, 0);
          removedCount++;
        } catch (error) {
          console.error(`Smart removal: Error removing item at line ${lineNumber}:`, error);
        }
      } else {
        console.warn(`Smart removal: Cannot remove item - missing line number or cart element`);
      }
    });
    
    // Force checkbox state to false regardless of cart state
    setTimeout(() => {
      const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
      if (giftWrapCheckbox) {
        giftWrapCheckbox.checked = false;
      }
      
      // Update active classes
      this.updatePackagingActiveClasses();
    }, 500);
    
  }

  // Ultra aggressive removal: Direct DOM manipulation
  ultraRemoveGiftWrap() {
    
    const cartItems = document.querySelectorAll('.cart-item');
    let removedCount = 0;
    
    // Find all gift wrap items
    const giftWrapItems = Array.from(cartItems).filter(item => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      return itemTitle.toLowerCase().includes('gift wrap');
    });
    
    
    // Method 1: Try cart.js updateQuantity for each item
    giftWrapItems.forEach((item, index) => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      const cartItemId = item.id;
      const lineNumber = cartItemId ? cartItemId.replace('CartItem-', '') : null;
      
      
      const cartItemsElement = document.querySelector('cart-items');
      if (cartItemsElement && lineNumber) {
        try {
          cartItemsElement.updateQuantity(lineNumber, 0);
          removedCount++;
        } catch (error) {
          console.error(`Ultra removal: Error with updateQuantity for line ${lineNumber}:`, error);
        }
      }
    });
    
    // Method 2: Direct quantity input manipulation
    setTimeout(() => {
      const remainingItems = document.querySelectorAll('.cart-item');
      const remainingGiftWrap = Array.from(remainingItems).filter(item => {
        const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
        return itemTitle.toLowerCase().includes('gift wrap');
      });
      
      
      remainingGiftWrap.forEach((item, index) => {
        const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
        
        // Try multiple selectors for quantity inputs
        const quantitySelectors = [
          'input[type="number"]',
          '.quantity__input',
          '.cart-item__quantity input',
          'input[name="quantity"]',
          '.quantity-input',
          'input[data-quantity]'
        ];
        
        quantitySelectors.forEach(selector => {
          const quantityInput = item.querySelector(selector);
          if (quantityInput) {
            quantityInput.value = 0;
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
      });
    }, 1000);
    
    // Method 3: Force state regardless of cart
    setTimeout(() => {
      
      // Force checkbox to unchecked
      const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
      if (giftWrapCheckbox) {
        giftWrapCheckbox.checked = false;
      }
      
      // Force active classes
      const complementaryCard = document.querySelector('.luxury-option-card[data-option-type="complementary"]');
      const giftWrapCard = document.querySelector('.luxury-option-card[data-option-type="giftwrap"]');
      
      if (complementaryCard) {
        complementaryCard.classList.add('active');
      }
      
      if (giftWrapCard) {
        giftWrapCard.classList.remove('active');
      }
      
    }, 2000);
    
  }

  // Nuclear option: Force remove by any means necessary
  nuclearRemoveGiftWrap() {
    
    // Step 1: Try to remove all gift wrap items from cart FIRST
    const cartItems = document.querySelectorAll('.cart-item');
    const giftWrapItems = Array.from(cartItems).filter(item => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      const variantId = item.getAttribute('data-variant-id');
      // Only include items with valid variant IDs (skip null variants)
      return itemTitle.toLowerCase().includes('gift wrap') && variantId && variantId !== 'null';
    });
    
    
    // Remove each gift wrap item
    giftWrapItems.forEach((item, index) => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      const variantId = item.getAttribute('data-variant-id');
      const cartItemId = item.id;
      const lineNumber = cartItemId ? cartItemId.replace('CartItem-', '').replace('CartDrawer-Item-', '') : null;
      
      
      const cartItemsElement = document.querySelector('cart-items');
      if (cartItemsElement && lineNumber) {
        try {
          cartItemsElement.updateQuantity(lineNumber, 0);
        } catch (error) {
          console.error(`Nuclear removal: Error removing item at line ${lineNumber}:`, error);
        }
      }
    });
    
    // Step 2: Force cart refresh and wait for it to complete
    setTimeout(() => {
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nuclear-removal' });
      }
    }, 500);
    
    // Step 3: Wait for cart update to complete, then update UI state
    setTimeout(() => {
      
      // Check if gift wrap is actually removed from cart
      const remainingCartItems = document.querySelectorAll('.cart-item');
      const remainingGiftWrap = Array.from(remainingCartItems).filter(item => {
        const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
        return itemTitle.toLowerCase().includes('gift wrap');
      });
      
      
      // Only update UI state if gift wrap is actually removed
      if (remainingGiftWrap.length === 0) {
        
        // Update checkbox state
        const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
        if (giftWrapCheckbox) {
          giftWrapCheckbox.checked = false;
        }
        
        // Update active classes
        const complementaryCard = document.querySelector('.luxury-option-card[data-option-type="complementary"]');
        const giftWrapCard = document.querySelector('.luxury-option-card[data-option-type="giftwrap"]');
        
        if (complementaryCard) {
          complementaryCard.classList.add('active');
        }
        
        if (giftWrapCard) {
          giftWrapCard.classList.remove('active');
        }
        
      } else {
      }
    }, 2000); // Wait 2 seconds for cart update to complete
    
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    this.eventListeners.forEach((listener, element) => {
      if (element && element.removeEventListener) {
        element.removeEventListener('change', listener);
      }
    });
    this.eventListeners.clear();

    this.operationQueue = [];
    this.isOperationInProgress = false;
  }

  // Add error recovery method
  recoverFromError() {
    this.enableAllCheckboxes();
    this.isSwitchingPackaging = false;
    this.restoreSelectedState();
    this.validateCartState();
    this.operationQueue = [];
    this.isOperationInProgress = false;
  }

  // Add timeout handling for all operations
  async withTimeout(operation, timeoutMs = 5000) {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Operation timeout')), timeoutMs);
    });

    return Promise.race([operation(), timeoutPromise]);
  }

  // Add comprehensive error handling wrapper
  async safeOperation(operation, errorContext = 'Operation') {
    try {
      return await this.withTimeout(operation);
    } catch (error) {
      console.error(`${errorContext} failed:`, error);
      this.recoverFromError();
      throw error;
    }
  }

  // Add memory leak prevention
  cleanupEventListeners() {
    this.eventListeners.forEach((listener, element) => {
      if (element && element.removeEventListener) {
        element.removeEventListener('change', listener);
      }
    });
    this.eventListeners.clear();
  }

  // Add periodic state validation
  startStateValidation() {
    // Validate state every 60 seconds to reduce performance impact
    setInterval(() => {
      if (!this.isOperationInProgress) {
        this.validateCartState();
        this.ensureSinglePackagingOption();
        this.updatePackagingActiveClasses();
        this.validateCartHasMainProduct();
      }
    }, 60000);
  }

  setupCartDrawerListener() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      const originalOpen = cartDrawer.open.bind(cartDrawer);
      cartDrawer.open = function(triggeredBy) {
        originalOpen(triggeredBy);
        
        setTimeout(() => {
          if (window.cartOptionsExtension) {
            window.cartOptionsExtension.updatePackagingActiveClasses();
          }
        }, 500);
        
        setTimeout(() => {
          if (window.cartOptionsExtension) {
            window.cartOptionsExtension.forceRefreshCartState();
          }
        }, 1000);
        
        setTimeout(() => {
          if (window.cartOptionsExtension) {
            window.cartOptionsExtension.validateCartHasMainProduct();
          }
        }, 1500);
      };
    }

    this.setupCartItemsObserver();
  }

  setupCartItemsObserver() {
    const cartItemsContainer = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (cartItemsContainer) {
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && 
              (mutation.target.classList.contains('cart-items') || 
               mutation.target.classList.contains('cart-drawer-items'))) {
            shouldUpdate = true;
          }
        });
        
        if (shouldUpdate) {
          setTimeout(() => {
            this.updatePackagingActiveClasses();
          }, 100);
        }
      });

      observer.observe(cartItemsContainer, {
        childList: true,
        subtree: true
      });
    }
  }

  // Method to sync cart drawer changes to cart page
  setupCartDrawerToPageSync() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.update === 'function') {
      const originalUpdate = cartDrawer.update.bind(cartDrawer);
      cartDrawer.update = function(sections) {
        originalUpdate(sections);
        
        setTimeout(() => {
          if (window.cartOptionsExtension) {
            window.cartOptionsExtension.syncCartPageSections(sections);
          }
        }, 100);
      };
    }
  }

  // Method to sync cart page sections
  syncCartPageSections(sections) {
    if (sections && sections.length > 0) {
      sections.forEach(section => {
        const sectionElement = document.querySelector(`[data-section-id="${section.id}"]`);
        if (sectionElement) {
          sectionElement.innerHTML = section.html;
        }
      });
      
      this.updateCartPageSections();
    }
  }

  // Method to update cart page sections
  updateCartPageSections() {
    const mainCartItems = document.querySelector('main-cart-items');
    const mainCartFooter = document.querySelector('main-cart-footer');
    const cartIconBubble = document.querySelector('cart-icon-bubble');
    
    if (mainCartItems) {
      mainCartItems.update();
    }
    
    if (mainCartFooter) {
      mainCartFooter.update();
    }
    
    if (cartIconBubble) {
      cartIconBubble.update();
    }
    
    setTimeout(() => {
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-sync' });
      }
    }, 200);
  }

  // Method to handle cart drawer quantity changes and sync to cart page
  setupCartDrawerQuantitySync() {
    document.addEventListener('change', (event) => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.hasAttribute('open')) {
        if (event.target.matches('input[type="number"], .quantity__input, .cart-item__quantity input')) {
          setTimeout(() => {
            this.updateCartPageSections();
          }, 500);
        }
      }
    });
    
    document.addEventListener('click', (event) => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.hasAttribute('open')) {
        const removeButton = event.target.closest('button[aria-label*="Remove"], button[aria-label*="Delete"], .cart-item__remove, .remove-button');
        if (removeButton) {
          setTimeout(() => {
            this.updateCartPageSections();
          }, 500);
        }
      }
    });
  }

  // Force refresh cart state and update active classes
  forceRefreshCartState() {
    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-options', forceRefresh: true });
    }
    
    setTimeout(() => {
      this.updatePackagingActiveClasses();
    }, 500);
  }

  setupPackagingOptions() {
    const packagingCheckboxes = document.querySelectorAll('.luxury-option-checkbox');

    if (packagingCheckboxes.length === 0) {
      return;
    }

    packagingCheckboxes.forEach(checkbox => {
      const packagingHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handlePackagingCheckboxChange(checkbox);
      };

      checkbox.addEventListener('change', packagingHandler);
      this.eventListeners.set(checkbox, packagingHandler);
    });
  }

  handlePackagingCheckboxChange(checkbox) {
    const optionType = checkbox.dataset.option;
    const variantId = checkbox.dataset.variantId;
    const isChecked = checkbox.checked;


    // Queue the operation to prevent race conditions
    this.queueOperation(async () => {
      // Check if this variant already exists in cart
      const existingCartItem = this.findCartItemByVariantId(variantId);

      if (isChecked) {
        // Checkbox was checked - handle packaging selection

        // Check if there's already a different packaging item in cart
        const existingPackagingItem = this.findAnyPackagingItem();
        if (existingPackagingItem) {
          // Check if this is a different packaging item (not the same variant)
          const existingVariantId = existingPackagingItem.getAttribute('data-variant-id');
          if (existingVariantId !== variantId) {
            // This is a switch between different packaging types
            const currentPackagingType = this.getPackagingTypeFromTitle(existingPackagingItem.querySelector('.cart-item__name')?.textContent || '');
            const newPackagingType = optionType === 'complementary' ? 'Complementary Packaging' : 'Gift Wrap Service';

            // Show appropriate alert message
            const alertMessage = optionType === 'complementary'
              ? `The current ${currentPackagingType} will be removed and ${newPackagingType} will be added instead. Do you want to continue?`
              : `The current ${currentPackagingType} will be removed and ${newPackagingType} will be added instead. Do you want to continue?`;

            const confirmed = confirm(alertMessage);

            if (!confirmed) {
              checkbox.checked = false;
              return;
            }

            // Disable all checkboxes during switch operation
            this.disableAllCheckboxes();

            // Set switching flag to prevent state restoration
            this.isSwitchingPackaging = true;

            // Remove existing packaging from cart
            await this.removeAllPackagingFromCart();

            // Wait for removal to complete
            await new Promise(resolve => setTimeout(resolve, 500));

            // Uncheck all packaging checkboxes
            document.querySelectorAll('.luxury-option-checkbox').forEach(otherCheckbox => {
              otherCheckbox.checked = false;
            });

            // Check the new checkbox
            checkbox.checked = true;

            // Handle based on the new option type
            if (optionType === 'giftwrap') {
              // For gift wrap, show message area and wait for user input
              const card = checkbox.closest('.luxury-option-card');
              if (card) {
                // Store the switching context for the gift wrap message area
                this.isSwitchingPackaging = true;
                this.showGiftWrapMessageArea(card, variantId, true); // Pass switching flag
              }
              // Re-enable checkboxes after showing message area
              setTimeout(() => {
                this.enableAllCheckboxes();
              }, 100);
            } else {
              // For complementary packaging, add directly to cart
              await this.addPackagingToCart(variantId, optionType);
              // Clear switching flag after successful add
              this.isSwitchingPackaging = false;
              // Re-enable checkboxes after adding
              setTimeout(() => {
                this.enableAllCheckboxes();
              }, 1000);
            }
            return;
          } else {
            // Same variant, just ensure checkbox is checked
            checkbox.checked = true;
            return;
          }
        }

        // If item already exists in cart, don't add again
        if (existingCartItem) {
          return;
        }

        // If no removal needed, proceed with normal selection
        await this.proceedWithPackagingSelection(checkbox, optionType, variantId);
      } else {
        // Checkbox was unchecked - remove packaging

        // Disable all checkboxes during removal
        this.disableAllCheckboxes();

        if (existingCartItem) {
          await this.removePackagingItemFromCart(existingCartItem);
        }

        // Hide gift wrap message area if exists
        const card = checkbox.closest('.luxury-option-card');
        if (card) {
          const messageArea = card.querySelector('.gift-wrap-message-area');
          if (messageArea) {
            messageArea.style.display = 'none';
          }
        }

        // Also hide any other gift wrap message areas
        document.querySelectorAll('.gift-wrap-message-area').forEach(area => {
          area.style.display = 'none';
        });

                        // If gift wrap was unchecked, automatically add complementary packaging
                if (optionType === 'giftwrap') {

                  // Use nuclear removal to handle stubborn gift wrap items
                  this.nuclearRemoveGiftWrap();
          
                  // Wait for cart update to complete and then add complementary packaging
                  setTimeout(async () => {
                    await this.addComplementaryPackagingAutomatically();
                  }, 3000); // Increased delay to ensure nuclear removal completes
                } else {
                  // For non-gift wrap items, update active classes immediately
                  this.updatePackagingActiveClasses();
                }

        // Re-enable checkboxes after removal
        setTimeout(() => {
          this.enableAllCheckboxes();
        }, 1000);
      }
    }).catch(error => {
      console.error('Packaging operation failed:', error);
      // Re-enable checkboxes on error
      this.enableAllCheckboxes();
    });
  }

  async proceedWithPackagingSelection(checkbox, optionType, variantId) {
    // Check if item already exists in cart before proceeding
    const existingCartItem = this.findCartItemByVariantId(variantId);
    if (existingCartItem) {
      return;
    }

    // Disable all checkboxes during operation
    this.disableAllCheckboxes();

    // Uncheck other packaging checkboxes
    document.querySelectorAll('.luxury-option-checkbox').forEach(otherCheckbox => {
      if (otherCheckbox !== checkbox) {
        otherCheckbox.checked = false;
      }
    });

    // Ensure the current checkbox is checked
    checkbox.checked = true;

    try {
      // Handle based on option type
      if (optionType === 'giftwrap') {
        // For gift wrap, show message area directly without alert
       
        const card = checkbox.closest('.luxury-option-card');
        if (card) {
          this.showGiftWrapMessageArea(card, variantId);
        }
        // Re-enable checkboxes after showing message area
        setTimeout(() => {
          this.enableAllCheckboxes();
        }, 100);
      } else {
        // For complementary packaging, add directly to cart
        await this.addPackagingToCart(variantId, optionType);
        // Re-enable checkboxes after adding
        setTimeout(() => {
          this.enableAllCheckboxes();
        }, 1000);
      }
    } catch (error) {
      console.error('Error in proceedWithPackagingSelection:', error);
      // Re-enable checkboxes on error
      this.enableAllCheckboxes();
      throw error;
    }
  }

  disableAllCheckboxes() {
    // Disable packaging checkboxes
    document.querySelectorAll('.luxury-option-checkbox').forEach(checkbox => {
      checkbox.disabled = true;
      checkbox.style.opacity = '0.5';
    });

    // Disable donation checkbox
    const donationCheckbox = document.getElementById('cart-donation');
    if (donationCheckbox) {
      donationCheckbox.disabled = true;
      donationCheckbox.style.opacity = '0.5';
    }
  }

  enableAllCheckboxes() {
    // Enable packaging checkboxes
    document.querySelectorAll('.luxury-option-checkbox').forEach(checkbox => {
      checkbox.disabled = false;
      checkbox.style.opacity = '1';
    });

    // Enable donation checkbox
    const donationCheckbox = document.getElementById('cart-donation');
    if (donationCheckbox) {
      donationCheckbox.disabled = false;
      donationCheckbox.style.opacity = '1';
    }
  }

  deselectPackagingOption(selectedCard) {

    selectedCard.classList.remove('selected');
    const btn = selectedCard.querySelector('.luxury-option-select-btn');
    if (btn) {
      btn.textContent = 'Select';
      btn.classList.remove('selected-btn');
    }

    // Hide gift wrap message area if exists
    const messageArea = selectedCard.querySelector('.gift-wrap-message-area');
    if (messageArea) {
      messageArea.style.display = 'none';
    }

    this.selectedPackagingOption = null;
    this.selectedVariantId = null; // Clear selected variant ID

    // Remove packaging from cart with a small delay to prevent immediate re-adding
    setTimeout(() => {
      this.removeAllPackagingFromCart();
    }, 100);
  }

  setupDonationOption() {
    const donationCheckbox = document.getElementById('cart-donation');
    if (donationCheckbox) {
      const donationHandler = (e) => {
        const variantId = e.target.dataset.variantId;

        this.queueOperation(async () => {
          this.disableAllCheckboxes();

          if (e.target.checked) {
            await this.addDonationToCart(variantId);
          } else {
            await this.removeDonationFromCart(variantId);
          }

          setTimeout(() => {
            this.enableAllCheckboxes();
          }, 1000);
        }).catch(error => {
          console.error('Donation operation failed:', error);
          this.enableAllCheckboxes();
        });
      };

      donationCheckbox.addEventListener('change', donationHandler);
      this.eventListeners.set(donationCheckbox, donationHandler);
    }
  }



  showGiftWrapMessageArea(selectedCard, variantId, isSwitching = false) {
    this.currentGiftWrapVariantId = variantId;

    // Check if this gift wrap item is already in cart
    const existingCartItem = this.findCartItemByVariantId(variantId);

    // Check if message area already exists
    let messageArea = selectedCard.querySelector('.gift-wrap-message-area');

    if (!messageArea) {
      // Create message area
      messageArea = document.createElement('div');
      messageArea.className = 'gift-wrap-message-area';
      messageArea.innerHTML = `
        <div class="gift-wrap-message-content">
          <div class="gift-wrap-textarea-wrapper">
            <textarea 
              class="gift-wrap-textarea" 
              placeholder="Enter your personal message here..."
              maxlength="199"
              rows="4"
            ></textarea>
            <div class="character-count">
              <span class="char-count">0</span>/199 characters
            </div>
          </div>
          <div class="gift-wrap-actions">
            <button type="button" class="gift-wrap-add-btn" disabled>Add to Bag</button>
          </div>
        </div>
      `;

      // Insert after the luxury-option-content
      const contentDiv = selectedCard.querySelector('.luxury-option-content');
      if (contentDiv) {
        contentDiv.parentNode.insertBefore(messageArea, contentDiv.nextSibling);
      }
    }

    // If item is already in cart, don't show message area
    if (existingCartItem) {
      messageArea.style.display = 'none';
      return;
    }

    // Show the message area
    messageArea.style.display = 'block';

    // Setup event listeners for the new elements
    const textarea = messageArea.querySelector('.gift-wrap-textarea');
    const charCount = messageArea.querySelector('.char-count');
    const addBtn = messageArea.querySelector('.gift-wrap-add-btn');

    if (textarea) {
      textarea.value = '';
      textarea.focus();

      // Character count and validation
      textarea.addEventListener('input', (e) => {
        const text = e.target.value;
        const cleanText = this.removeSymbols(text);

        if (text !== cleanText) {
          e.target.value = cleanText;
        }

        const count = cleanText.length;
        charCount.textContent = count;

        if (addBtn) {
          addBtn.disabled = count === 0;
        }
      });
    }

    if (addBtn) {
      addBtn.addEventListener('click', async () => {
        const message = textarea ? textarea.value.trim() : '';
        if (message) {

          // Disable all checkboxes during add operation
          this.disableAllCheckboxes();

          // Hide message area immediately
          messageArea.style.display = 'none';

          try {
            // Remove any existing packaging first (complementary or other gift wrap)
            await this.removeAllPackagingFromCart();

            // Wait for removal to complete
            await new Promise(resolve => setTimeout(resolve, 500));

            await this.addGiftWrapToCart(variantId, message);

            // Show success message
            this.showSuccessMessage(selectedCard, 'Gift wrap added to bag successfully!');

            // Clear switching flag after successful add
            setTimeout(() => {
              this.isSwitchingPackaging = false;
            }, 2000); // Clear flag after 2 seconds to prevent interference

            // Uncheck all other packaging checkboxes
            document.querySelectorAll('.luxury-option-checkbox').forEach(otherCheckbox => {
              if (otherCheckbox.dataset.variantId !== variantId) {
                otherCheckbox.checked = false;
              }
            });

            // Ensure checkbox stays checked and re-enable checkboxes after adding
            setTimeout(() => {
              const checkbox = document.querySelector(`.luxury-option-checkbox[data-variant-id="${variantId}"]`);
              if (checkbox) {
                checkbox.checked = true;
              }
              this.enableAllCheckboxes();
            }, 1000);
          } catch (error) {
            console.error('Error adding gift wrap:', error);
            // Clear switching flag on error
            this.isSwitchingPackaging = false;
            // Re-enable checkboxes on error
            this.enableAllCheckboxes();
            // Show message area again on error
            messageArea.style.display = 'block';
          }
        }
      });
    }
  }

  showSuccessMessage(card, message) {
    // Remove any existing success message
    const existingMessage = card.querySelector('.success-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create success message
    const successMessage = document.createElement('div');
    successMessage.className = 'success-message';
    successMessage.style.cssText = `
      background-color: #d4edda;
      color: #155724;
      padding: 10px;
      margin: 10px 0;
      border-radius: 4px;
      border: 1px solid #c3e6cb;
      text-align: center;
      font-weight: 500;
    `;
    successMessage.textContent = message;

    // Insert after the luxury-option-content
    const contentDiv = card.querySelector('.luxury-option-content');
    if (contentDiv) {
      contentDiv.parentNode.insertBefore(successMessage, contentDiv.nextSibling);
    }

    // Remove success message after 3 seconds
    setTimeout(() => {
      if (successMessage.parentNode) {
        successMessage.remove();
      }
    }, 3000);
  }



  removeSymbols(text) {
    // Remove emojis and special symbols, keep only letters, numbers, spaces, and basic punctuation
    return text.replace(/[^\w\s.,!?;:'"()-]/g, '');
  }

  async addPackagingToCart(variantId, optionType) {

    // Validate variant ID
    if (!variantId || variantId === '' || variantId === 'undefined') {
      console.error('Invalid variant ID for packaging:', variantId);
      throw new Error('Invalid variant ID for packaging');
    }

    // Track this variant as a packaging item
    this.packagingVariantIds.add(variantId);

    try {
      // Add cart attribute for tracking
      await this.addCartAttribute(optionType, optionType === 'complementary' ? 'Complementary Packaging' : 'Gift Wrap Service');

      // Use native cart.js method to add item - same as donation
      await this.addItemToCart(variantId, 1);

    } catch (error) {
      console.error('Error adding packaging:', error);
      // Remove from tracking on error
      this.packagingVariantIds.delete(variantId);
      // Revert selection on error
      if (this.selectedPackagingOption) {
        this.deselectPackagingOption(this.selectedPackagingOption);
      }
      throw error;
    }
  }

  async addGiftWrapToCart(variantId, message) {
    
    try {
      // Set a flag to prevent complementary packaging from being added
      this.preventComplementaryPackaging = true;
      // Set a flag to prevent single packaging validation from interfering
      this.preventSinglePackagingValidation = true;
      
      // Always remove any existing packaging items first
      const existingPackagingItem = this.findAnyPackagingItem();
      if (existingPackagingItem) {
        await this.removeAllPackagingFromCart();
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Track this variant as a packaging item
      this.packagingVariantIds.add(variantId);
      
      // Add cart attribute for tracking
      await this.addCartAttribute('giftwrap', 'Gift Wrap Service');
      
      // Add the gift message to cart attributes/notes
      if (message && message.trim()) {
        await this.addCartAttribute('Gift Wrap Message', message);
      }
      
      // Add item to cart with Gift Wrap Message property
      await this.addItemToCartWithProperties(variantId, 1, { 'Gift Wrap Message': message });
      
      // Hide all gift wrap message areas immediately after adding
      document.querySelectorAll('.gift-wrap-message-area').forEach(area => {
        area.style.display = 'none';
      });
      
      // Uncheck all other packaging checkboxes
      document.querySelectorAll('.luxury-option-checkbox').forEach(otherCheckbox => {
        if (otherCheckbox.dataset.variantId !== variantId) {
          otherCheckbox.checked = false;
        }
      });
      
      // Update active classes immediately after adding gift wrap
      this.updatePackagingActiveClasses();
      
      // Ensure the checkbox stays checked after successful add
      setTimeout(() => {
        const checkbox = document.querySelector(`.luxury-option-checkbox[data-variant-id="${variantId}"]`);
        if (checkbox) {
          checkbox.checked = true;
        }
        
        this.isSwitchingPackaging = false;
        // Clear the prevention flags after gift wrap is properly added
        this.preventComplementaryPackaging = false;
        this.preventSinglePackagingValidation = false;
        
        // Update active classes again to ensure consistency
        this.updatePackagingActiveClasses();
      }, 3000); // Increased delay to ensure cart update is complete
      
    } catch (error) {
      console.error('Error adding gift wrap:', error);
      this.packagingVariantIds.delete(variantId);
      this.preventComplementaryPackaging = false; // Clear flag on error
      this.preventSinglePackagingValidation = false; // Clear flag on error
      throw error;
    }
  }  
  

  removeAllPackagingFromCart() {

    return new Promise(async (resolve) => {
      // Remove cart attributes
      this.removeCartAttribute('complementary');
      this.removeCartAttribute('giftwrap');
      this.removeCartAttribute('Gift Wrap Message');

      // Find and remove packaging items from cart using cart.js native method
      const cartItems = document.querySelectorAll('.cart-item');
      let removedItems = false;


      // Process items in reverse order to avoid line number issues
      const itemsToProcess = Array.from(cartItems).reverse();

      for (const item of itemsToProcess) {
        const variantId = item.getAttribute('data-variant-id');
        const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';

        // Get the correct line number from the cart item ID
        const cartItemId = item.id;
        const lineNumber = cartItemId ? cartItemId.replace('CartItem-', '') : null;


        // Only process items with valid variant IDs
        if (variantId && variantId !== 'null' && variantId !== 'undefined' && lineNumber) {
          const cartItemsElement = document.querySelector('cart-items');
          if (cartItemsElement) {
            // Check if this item is a packaging item by:
            // 1. Checking if it's in our tracked packaging variants
            // 2. Checking the product title
            const isTrackedPackaging = this.packagingVariantIds.has(variantId);
            const isPackagingByTitle = this.isPackagingItem(itemTitle);


            if (isTrackedPackaging || isPackagingByTitle) {
              try {
                // Use cart.js native updateQuantity method with correct line number
                cartItemsElement.updateQuantity(lineNumber, 0);
                removedItems = true;

                // Remove from tracking
                this.packagingVariantIds.delete(variantId);


                // Wait a moment for the removal to process
                await new Promise(resolve => setTimeout(resolve, 200));
              } catch (error) {
                console.error(`Error removing item at line ${lineNumber}:`, error);
              }
            } else {
            }
          }
        } else {
        }
      }

      // Clear all tracked packaging variants
      this.packagingVariantIds.clear();

      // Wait for cart.js to complete the removal and then resolve
      setTimeout(() => {
        
        // Update active classes after removing all packaging
        this.updatePackagingActiveClasses();
        
        resolve();
      }, 1000);
    });
  }

  removePackagingFromCart() {

    // Remove cart attributes
    this.removeCartAttribute('complementary');
    this.removeCartAttribute('giftwrap');
      this.removeCartAttribute('Gift Wrap Message');

    // Find and remove packaging items from cart by checking cart attributes
    const cartItems = document.querySelectorAll('.cart-item');
    cartItems.forEach(item => {
      const variantId = item.getAttribute('data-variant-id');
      if (variantId) {
        // Only remove if this is a packaging item (check by variant ID or cart attributes)
        const cartItemsElement = document.querySelector('cart-items');
        if (cartItemsElement && item.dataset.index) {
          // Check if this item is a packaging item by looking at the product title or variant
          const itemTitle = item.querySelector('.cart-item__name')?.textContent?.toLowerCase() || '';
          const isPackagingItem = itemTitle.includes('packaging') || itemTitle.includes('gift wrap') || itemTitle.includes('wrapping');

          if (isPackagingItem) {
            cartItemsElement.updateQuantity(item.dataset.index, 0);
          }
        }
      }
    });
  }

  addDonationToCart(variantId) {

    // Validate variant ID
    if (!variantId || variantId === '' || variantId === 'undefined') {
      console.error('Invalid variant ID for donation:', variantId);
      return;
    }

    // Track this variant as a donation item
    this.donationVariantIds = this.donationVariantIds || new Set();
    this.donationVariantIds.add(variantId);

    // Add cart attribute for tracking
    this.addCartAttribute('donation', 'Donation').then(() => {
      // Add item to cart using native cart.js method
      this.addItemToCart(variantId, 1);
    }).catch(error => {
      console.error('Error adding donation:', error);
      // Re-check the checkbox on error
      const checkbox = document.getElementById('cart-donation');
      if (checkbox) {
        checkbox.checked = false;
      }
    });
  }

  removeDonationFromCart(variantId) {

    // Remove cart attribute
    this.removeCartAttribute('donation');

    // Find and remove donation item from cart using cart.js native method
    const cartItems = document.querySelectorAll('.cart-item');
    let removedItems = false;


    cartItems.forEach(item => {
      const itemVariantId = item.getAttribute('data-variant-id');
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';


      if (itemVariantId === variantId) {
        const cartItemsElement = document.querySelector('cart-items');
        if (cartItemsElement) {
          // Get the correct line number from the cart item ID
          const cartItemId = item.id;
          const lineNumber = cartItemId ? cartItemId.replace('CartItem-', '') : null;

          if (lineNumber) {
            // Double check this is actually a donation item
            const isDonationItem = itemTitle.toLowerCase().includes('donation') ||
              itemTitle.toLowerCase().includes('charity') ||
              itemTitle.toLowerCase().includes('support');


            if (isDonationItem || itemVariantId === variantId) {
              try {
                // Use cart.js native updateQuantity method with correct line number
                cartItemsElement.updateQuantity(lineNumber, 0);
                removedItems = true;

                // Remove from tracking
                if (this.donationVariantIds) {
                  this.donationVariantIds.delete(variantId);
                }
              } catch (error) {
                console.error(`Error removing donation item at line ${lineNumber}:`, error);
              }
            }
          } else {
            console.warn('Cannot remove donation item - missing line number');
          }
        }
      }
    });

    // Re-setup event listeners after DOM update if items were removed
    if (removedItems) {
      setTimeout(() => {
        this.setupEventListeners();
      }, 200);
    } else {
    }
  }

  addItemToCart(variantId, quantity) {
    // Use native cart.js method to add item
    const cartItems = document.querySelector('cart-items');
    if (!cartItems) {
      console.error('Cart items element not found');
      return Promise.reject(new Error('Cart items element not found'));
    }

    // Use the native cart add functionality with cart.js sections
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    // Use cart.js getSectionsToRender() method to get the same sections as cart.js
    formData.append('sections', cartItems.getSectionsToRender().map((section) => section.section).join(','));
    formData.append('sections_url', window.location.pathname);

    config.body = formData;

    // Add timeout handling
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
    });

    return Promise.race([
      fetch(`${routes.cart_add_url}`, config)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            console.error('Error adding cart item:', response);
            throw new Error('Cart add failed');
          }


          // Let cart.js handle the section updates naturally (same as cart.js does)
          if (response.sections) {
            this.updateCartSections(response.sections);
          }

          // Publish cart update event (same as cart.js does)
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-options', cartData: response });
          }

          // Re-setup event listeners after DOM update
          setTimeout(() => {
            this.setupEventListeners();
          }, 100);

          return response;
        }),
      timeoutPromise
    ]).catch((e) => {
      console.error('Error adding cart item:', e);
      // Revert selection on error
      if (this.selectedPackagingOption) {
        this.deselectPackagingOption(this.selectedPackagingOption);
      }
      throw e;
    });
  }

  addCartItem(variantId, quantity) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    // Add sections for cart update
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      formData.append(
        'sections',
        cartItems.getSectionsToRender().map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((response) => {
        if (response.status) {
          console.error('Error adding cart item:', response);
          return;
        }

        // Update cart sections using cart.js method
        if (response.sections) {
          this.updateCartSections(response.sections);
        }

        // Publish cart update event to trigger cart.js update
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-options', cartData: response });
        }

        // Force cart footer update
        this.updateCartFooter();
      })
      .catch((e) => {
        console.error('Error adding cart item:', e);
      });
  }

  async addItemToCartWithProperties(variantId, quantity, properties) {
    // Use native cart.js method to add item with properties
    const cartItems = document.querySelector('cart-items');
    if (!cartItems) {
      console.error('Cart items element not found');
      return Promise.reject(new Error('Cart items element not found'));
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    // Add properties
    Object.keys(properties).forEach(key => {
      formData.append(`properties[${key}]`, properties[key]);
    });

    // Use cart.js getSectionsToRender() method to get the same sections as cart.js
    formData.append('sections', cartItems.getSectionsToRender().map((section) => section.section).join(','));
    formData.append('sections_url', window.location.pathname);

    config.body = formData;

    // Add timeout handling
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
    });

    return Promise.race([
      fetch(`${routes.cart_add_url}`, config)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            console.error('Error adding cart item with properties:', response);
            throw new Error('Cart add with properties failed');
          }


          // Let cart.js handle the section updates naturally (same as cart.js does)
          if (response.sections) {
            this.updateCartSections(response.sections);
          }

          // Publish cart update event (same as cart.js does)
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-options', cartData: response });
          }

          // Re-setup event listeners after DOM update
          setTimeout(() => {
            this.setupEventListeners();
          }, 100);

          return response;
        }),
      timeoutPromise
    ]).catch((e) => {
      console.error('Error adding cart item with properties:', e);
      throw e;
    });
  }

  addCartItemWithProperties(variantId, quantity, properties) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    // Add properties
    Object.keys(properties).forEach(key => {
      formData.append(`properties[${key}]`, properties[key]);
    });

    // Add sections for cart update
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      formData.append(
        'sections',
        cartItems.getSectionsToRender().map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((response) => {
        if (response.status) {
          console.error('Error adding cart item with properties:', response);
          return;
        }

        // Update cart sections
        if (response.sections) {
          this.updateCartSections(response.sections);
        }

        // Publish cart update event
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-options', cartData: response });
        }

        // Force cart footer update
        this.updateCartFooter();
      })
      .catch((e) => {
        console.error('Error adding cart item with properties:', e);
      });
  }

  addCartAttribute(key, value) {
    const body = JSON.stringify({
      attributes: {
        [key]: value
      }
    });

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = body;

    return fetch(`${routes.cart_update_url}`, config)
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          console.error('Error adding cart attribute:', response);
          return;
        }
      })
      .catch(error => {
        console.error('Error adding cart attribute:', error);
      });
  }

  removeCartAttribute(key) {
    const body = JSON.stringify({
      attributes: {
        [key]: ''
      }
    });

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = body;

    return fetch(`${routes.cart_update_url}`, config)
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          console.error('Error removing cart attribute:', response);
          return;
        }
      })
      .catch(error => {
        console.error('Error removing cart attribute:', error);
      });
  }

  updateCartSections(sections) {
    if (!sections) return;


    // Use the exact same logic as cart.js for updating sections
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      cartItems.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

        if (elementToReplace && sections[section.section]) {
          const html = new DOMParser().parseFromString(sections[section.section], 'text/html');
          const newContent = html.querySelector(section.selector);

          if (newContent) {
            elementToReplace.innerHTML = newContent.innerHTML;

            // Log cart footer update specifically
            if (section.id === 'main-cart-footer') {
            }
          }
        }
      });
    }

    // Also update cart icon bubble if it exists
    const cartIconBubble = document.querySelector('cart-icon-bubble');
    if (cartIconBubble && typeof cartIconBubble.renderContents === 'function') {
      cartIconBubble.renderContents();
    }

    // Re-attach event listeners after DOM update
    setTimeout(() => {
      this.setupEventListeners();
    }, 100);
  }

  updateCartFooter() {

    // Fetch and update cart footer section
    fetch(`${routes.cart_url}?section_id=main-cart-footer`)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceCartFooter = html.querySelector('#main-cart-footer');

        if (sourceCartFooter) {
          const currentCartFooter = document.querySelector('#main-cart-footer');
          if (currentCartFooter) {
            // Only update the totals section, not the entire footer
            const sourceTotals = sourceCartFooter.querySelector('.totals');
            const currentTotals = currentCartFooter.querySelector('.totals');

            if (sourceTotals && currentTotals) {
              currentTotals.innerHTML = sourceTotals.innerHTML;
            } else {
              console.warn('Totals section not found in cart footer');
            }
          } else {
            console.warn('Current cart footer element not found');
          }
        } else {
          console.warn('Source cart footer element not found in response');
        }
      })
      .catch(error => {
        console.error('Error refreshing cart footer:', error);
      });
  }



  updateCartTotal() {
    // Trigger cart update event to refresh totals
    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-options' });
    }

    // Also try to refresh cart sections manually
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      cartItems.onCartUpdate();
    }

    // Force cart footer update
    this.updateCartFooter();
  }

  handleCartStateUpdate(parsedState) {
    // Handle cart state updates like cart.js does
    const cartItems = document.querySelector('cart-items');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartItems) {
      cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    if (cartFooter) {
      cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    // Update sections if provided
    if (parsedState.sections) {
      this.updateCartSections(parsedState.sections);
    }
  }

  forceCartFooterUpdate() {

    // Use Shopify's native cart update mechanism - let cart.js handle everything
    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-options' });
    }
  }

  hideQuantitySelectorsForSpecialItems() {
    // Hide quantity selectors for Complementary, Gift Wrap, and Donation products
    const cartItems = document.querySelectorAll('.cart-item');

    cartItems.forEach(item => {
      const variantId = item.getAttribute('data-variant-id');
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      const quantitySelector = item.querySelector('.cart-item__quantity');

      if (quantitySelector) {
        // Check if this is a special item (packaging or donation)
        const isPackagingItem = itemTitle.toLowerCase().includes('packaging') ||
          itemTitle.toLowerCase().includes('gift wrap') ||
          itemTitle.toLowerCase().includes('wrapping') ||
          itemTitle.toLowerCase().includes('gift') ||
          itemTitle.toLowerCase().includes('luxury') ||
          itemTitle.toLowerCase().includes('gift wrap service') ||
          itemTitle.toLowerCase().includes('gift wrapping') ||
          itemTitle.toLowerCase().includes('complementary');

        const isDonationItem = itemTitle.toLowerCase().includes('donation') ||
          itemTitle.toLowerCase().includes('charity') ||
          itemTitle.toLowerCase().includes('support');

      }
    });
  }

  // Add this method to properly check if gift wrap is currently selected
  isGiftWrapSelected() {
    // Check if gift wrap is actually in the cart (this is the primary source of truth)
    const giftWrapInCart = Array.from(document.querySelectorAll('.cart-item')).some(item => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      return itemTitle.toLowerCase().includes('gift wrap');
    });

    return giftWrapInCart;
  }

  // New method to ensure complementary packaging is added by default
  async ensureDefaultPackaging() {
    if (this.isSwitchingPackaging || this.preventComplementaryPackaging || this.isGiftWrapSelected()) {
      return;
    }

    const cartItems = document.querySelectorAll('.cart-item');
    const hasRegularItems = Array.from(cartItems).some(item => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      return !this.isPackagingItem(itemTitle) && !this.isDonationItem(itemTitle);
    });

    if (!hasRegularItems) {
      return;
    }

    const hasPackagingInCart = this.findAnyPackagingItem();
    if (!hasPackagingInCart) {
      return;
    }
  }

  // Helper methods to identify item types
  isPackagingItem(itemTitle) {
    const lowerTitle = itemTitle.toLowerCase();
    return lowerTitle.includes('packaging') ||
      lowerTitle.includes('gift wrap') ||
      lowerTitle.includes('wrapping') ||
      lowerTitle.includes('gift') ||
      lowerTitle.includes('luxury') ||
      lowerTitle.includes('gift wrap service') ||
      lowerTitle.includes('gift wrapping') ||
      lowerTitle.includes('complementary');
  }

  isDonationItem(itemTitle) {
    const lowerTitle = itemTitle.toLowerCase();
    return lowerTitle.includes('donation') ||
      lowerTitle.includes('charity') ||
      lowerTitle.includes('support');
  }

  // Method to automatically add complementary packaging
  async addComplementaryPackagingAutomatically() {
    const complementaryCheckbox = document.querySelector('.luxury-option-checkbox[data-option="complementary"]');
    if (!complementaryCheckbox) {
      return;
    }

    const variantId = complementaryCheckbox.dataset.variantId;
    const existingComplementary = this.findCartItemByVariantId(variantId);

    if (existingComplementary) {
      complementaryCheckbox.checked = true;
      return;
    }

    const existingGiftWrap = this.findCartItemByVariantId(this.currentGiftWrapVariantId);
    if (existingGiftWrap) {
      await this.removePackagingItemFromCart(existingGiftWrap);
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    try {
      await this.addPackagingToCart(variantId, 'complementary');
      complementaryCheckbox.checked = true;

      const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
      if (giftWrapCheckbox) {
        giftWrapCheckbox.checked = false;
      }

      this.updatePackagingActiveClasses();
    } catch (error) {
      console.error('Error adding complementary packaging automatically:', error);
    }
  }

  // Method to update active classes for packaging options
  updatePackagingActiveClasses() {
    const cartItems = document.querySelectorAll('.cart-item');
    
    const giftWrapInCart = Array.from(cartItems).some(item => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      const variantId = item.getAttribute('data-variant-id');
      return itemTitle.toLowerCase().includes('gift wrap') && variantId && variantId !== 'null';
    });
    
    // Update checkbox state to match cart state
    const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
    if (giftWrapCheckbox) {
      giftWrapCheckbox.checked = giftWrapInCart;
    }
    
    // Update complementary packaging card
    const complementaryCard = document.querySelector('.luxury-option-card[data-option-type="complementary"]');
    if (complementaryCard) {
      complementaryCard.classList.toggle('active', !giftWrapInCart);
    }
    
    // Update gift wrap card
    const giftWrapCard = document.querySelector('.luxury-option-card[data-option-type="giftwrap"]');
    if (giftWrapCard) {
      giftWrapCard.classList.toggle('active', giftWrapInCart);
    }
  }

  // Delayed update with verification
  updatePackagingActiveClassesWithDelay(delay = 1000) {
    setTimeout(() => {
      const cartItems = document.querySelectorAll('.cart-item');
      const giftWrapInCart = Array.from(cartItems).some(item => {
        const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
        return itemTitle.toLowerCase().includes('gift wrap');
      });
      
      // Update checkbox state to match actual cart state
      const giftWrapCheckbox = document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
      if (giftWrapCheckbox) {
        giftWrapCheckbox.checked = giftWrapInCart;
      }
      
      // Update active classes based on actual cart state
      const complementaryCard = document.querySelector('.luxury-option-card[data-option-type="complementary"]');
      const giftWrapCard = document.querySelector('.luxury-option-card[data-option-type="giftwrap"]');
      
      if (complementaryCard) {
        complementaryCard.classList.toggle('active', !giftWrapInCart);
      }
      
      if (giftWrapCard) {
        giftWrapCard.classList.toggle('active', giftWrapInCart);
      }
    }, delay);
  }

  // Method to ensure only one packaging option is active
  async ensureSinglePackagingOption() {
    if (this.isSwitchingPackaging || this.preventSinglePackagingValidation || this.isGiftWrapSelected()) {
      return;
    }

    const cartItems = document.querySelectorAll('.cart-item');
    const packagingItems = Array.from(cartItems).filter(item => {
      const itemTitle = item.querySelector('.cart-item__name')?.textContent?.trim() || '';
      return this.isPackagingItem(itemTitle);
    });

    if (packagingItems.length > 1) {
      await this.removeAllPackagingFromCart();
      await new Promise(resolve => setTimeout(resolve, 500));
      await this.addComplementaryPackagingAutomatically();
    } else if (packagingItems.length === 1) {
      const packagingItem = packagingItems[0];
      const itemTitle = packagingItem.querySelector('.cart-item__name')?.textContent?.trim() || '';

      if (this.isPackagingItem(itemTitle)) {
        const isComplementary = itemTitle.toLowerCase().includes('complementary');
        const isGiftWrap = itemTitle.toLowerCase().includes('gift wrap') || itemTitle.toLowerCase().includes('gift wrap service');

        // Uncheck all packaging checkboxes first
        document.querySelectorAll('.luxury-option-checkbox').forEach(checkbox => {
          checkbox.checked = false;
        });

        // Check the appropriate checkbox
        const targetCheckbox = isComplementary 
          ? document.querySelector('.luxury-option-checkbox[data-option="complementary"]')
          : document.querySelector('.luxury-option-checkbox[data-option="giftwrap"]');
        
        if (targetCheckbox) {
          targetCheckbox.checked = true;
        }
      }
    } else {
      await this.addComplementaryPackagingAutomatically();
    }
  }
}
const style = document.createElement('style');
style.textContent = ``;
document.head.appendChild(style);

// Initialize cart options extension when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Prevent multiple initializations
  if (!window.cartOptionsExtension) {
    window.cartOptionsExtension = new CartOptionsExtension();
  }
});

// Clean up on page unload to prevent memory leaks
window.addEventListener('beforeunload', () => {
  if (window.cartOptionsExtension) {
    window.cartOptionsExtension.destroy();
  }
}); 